/**
 * Layer 10 Evidence Dashboard (Phase 8).
 *
 * Single recruiter-facing page that surfaces the FULL per-signal evidence
 * for one candidate's coding test. Reads everything from
 * GET /api/cheating/dashboard/<test_id> in one round-trip.
 *
 * Architecture rationale: the old single-number `plagiarism_percentage`
 * column muddled three different signals (MOSS cross-candidate similarity,
 * LLM judge AI-generation likelihood, LLM judge online-source likelihood)
 * into one percentage. This page reads from cheating_events directly and
 * shows each signal in its own panel with its own evidence — so recruiters
 * can actually understand WHY the candidate was flagged, not just THAT
 * they were flagged.
 *
 * Tabs:
 *   • Signals    — code plagiarism per question (MOSS / AI / online / style)
 *   • Identity   — ID photo, selfie, CompareFaces match, liveness
 *   • Snapshots  — Rekognition snapshot filmstrip with detected labels
 *   • Timeline   — chronological list of every cheating_events row
 *
 * The MOSS diff viewer (side-by-side code) is a modal opened from the
 * Signals tab — kept in a sibling file (MossDiffModal.jsx) to keep this
 * file readable.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ShieldAlert, ShieldCheck, AlertTriangle, CheckCircle2, ChevronDown,
  ChevronRight, Eye, Code2, User, Camera, Clock, Cpu, Brain, FileSearch,
  ArrowLeft, X, ExternalLink, Hash, Download, Loader2,
  // Icon for the new "Code Understanding" tab — same MessageSquare we
  // use on the candidate-side FollowupCallModal modal header, so the
  // recruiter and candidate UIs share visual language.
  MessageSquare,
} from 'lucide-react';
import { API_BASE_URL } from '@/config/api';
import MossDiffModal from './MossDiffModal';
import CodeUnderstandingTab from './CodeUnderstandingTab';

// ─── Severity → Tailwind class lookup ────────────────────────────────────────
// Single source of truth for badge colors so the four tabs stay consistent.
const SEVERITY_BADGE = {
  info:     'bg-slate-100 text-slate-700 border-slate-200',
  low:      'bg-blue-100 text-blue-700 border-blue-200',
  medium:   'bg-amber-100 text-amber-700 border-amber-200',
  high:     'bg-orange-100 text-orange-700 border-orange-200',
  critical: 'bg-red-100 text-red-700 border-red-200',
};

// Verdict band → Tailwind classes for the big header chip.
const VERDICT_BAND = {
  clean: {
    label: 'Clean',
    cls: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    icon: <ShieldCheck className="w-5 h-5" />,
    sub: 'No significant cheating signals detected.',
  },
  minor: {
    label: 'Minor signals',
    cls: 'bg-blue-50 border-blue-200 text-blue-700',
    icon: <CheckCircle2 className="w-5 h-5" />,
    sub: 'A few low-severity events. Likely benign.',
  },
  suspicious: {
    label: 'Suspicious',
    cls: 'bg-amber-50 border-amber-200 text-amber-800',
    icon: <AlertTriangle className="w-5 h-5" />,
    sub: 'Multiple signals or one critical event — manual review recommended.',
  },
  high_confidence: {
    label: 'High confidence',
    cls: 'bg-red-50 border-red-300 text-red-700',
    icon: <ShieldAlert className="w-5 h-5" />,
    sub: 'Strong evidence of cheating. Recommended action: reject or re-test.',
  },
};

// Tabs config — adding a new tab is just appending here + a render function.
const TABS = [
  { id: 'signals',       label: 'Code Signals',       icon: Code2 },
  // Phase 5 — surfaces the post-coding-question voice follow-up data
  // captured in code_fingerprints.followup_session. Sits right after
  // Code Signals because both are code-submission-focused — recruiters
  // can scan plagiarism + comprehension together.
  { id: 'understanding', label: 'Code Understanding', icon: MessageSquare },
  { id: 'identity',      label: 'Identity',           icon: User },
  { id: 'snapshots',     label: 'Snapshots',          icon: Camera },
  { id: 'baseline',      label: 'Room Scan',          icon: FileSearch },
  { id: 'timeline',      label: 'Timeline',           icon: Clock },
];

// Vendor / library namespaces we must NEVER surface to users — showing them
// leaks which detection tech powers proctoring, which is exactly what a
// cheating-detection product should not reveal. Keys under these prefixes are
// stripped to a neutral, human-readable label instead.
const TECH_PREFIXES = new Set([
  'mediapipe', 'tensorflow', 'tf', 'tflite', 'rekognition', 'aws',
  'opencv', 'cv', 'yolo', 'dlib', 'mtcnn', 'onnx', 'blazeface',
]);

const humanize = (s) =>
  String(s || '')
    .replace(/[._]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const niceFeatureName = (key) => {
  if (!key) return 'Unknown signal';
  // Curated, neutral labels for the signals recruiters see most — note NONE
  // name the underlying library.
  const map = {
    'code.moss_similarity':       'Cross-candidate similarity',
    'code.ai_signature_v2':       'AI / online plagiarism judge',
    'code.cross_question_style':  'Cross-question style shift',
    'snapshots.detect_faces':     'Face detection',
    'snapshots.detect_labels':    'Object detection',
    'snapshots.detect_text':      'Text on screen',
    'snapshots.compare_faces':    'Identity match (snapshot)',
    'identity.id_upload':         'ID upload',
    'identity.face_match':        'Identity face match',
    'identity.liveness_check':    'Liveness check',
    'lockdown.devtools':          'DevTools detection',
    'lockdown.virtual_camera':    'Virtual camera',
    'lockdown.multi_monitor':     'Multi-monitor',
    'face.presence':              'Face presence',
    'face.multiple_faces':        'Multiple faces',
    'face.head_pose':             'Head pose',
    'face.eye_gaze':              'Eye gaze',
    'hand.detection':             'Hand detection',
    'audio.classification':       'Audio classification',
    // Common vendor-namespaced variants mapped to the same neutral labels.
    'mediapipe.face_presence':    'Face presence',
    'mediapipe.multiple_faces':   'Multiple faces',
    'mediapipe.head_pose':        'Head pose',
    'mediapipe.eye_gaze':         'Eye gaze',
    'mediapipe.hand_detection':   'Hand detection',
    'mediapipe.hand_presence':    'Hand detection',
    'mediapipe.hand_tracking':    'Hand detection',
    'mediapipe.audio_classifier': 'Audio classification',
  };
  if (map[key]) return map[key];

  // Fallback: strip a leading vendor/library segment so we never leak the tech,
  // then humanize whatever remains (e.g. "mediapipe.eye_gaze" -> "Eye Gaze").
  const parts = String(key).split('.');
  if (parts.length > 1 && TECH_PREFIXES.has(parts[0].toLowerCase())) {
    return humanize(parts.slice(1).join(' ')) || 'Signal';
  }
  return humanize(key) || 'Signal';
};

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

// ─── Page entry point ────────────────────────────────────────────────────────
export default function CheatingEvidence({
  // Embedded mode — set to true when rendering INSIDE another page
  // (e.g. ApplicationList renders this in-place of the candidate list
  // when the recruiter clicks the "Suspicious / Cheating Detected" chip).
  // Mirrors the Reports.jsx `embedded` prop pattern so the two pages
  // behave consistently inside the same shell.
  embedded = false,
  // When embedded, the parent passes testId here instead of via URL
  // params (because we're not navigating, just changing what gets
  // rendered in the same content area).
  testIdProp = null,
  // When embedded, "Back" should return the parent to the candidate
  // list, not the browser's history stack. Parent passes this in.
  onClose = null,
  // When embedded there is no ?type= URL param, so the parent passes the test
  // type here (screening/pnb/communication/apt) to hide the coding-only tabs.
  typeProp = null,
} = {}) {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // testId can come from path param `/cheating-evidence/:testId` or from
  // `?test_id=...` query string — accept both for entry-point flexibility.
  // When embedded, the parent-passed prop wins over URL state.
  const testId = (
    testIdProp ||
    params.testId ||
    searchParams.get('test_id') ||
    ''
  );

  // Non-coding tests (aptitude, screening, personality/behavioural,
  // communication — entered with ?type=apt|screening|pnb|communication) have no
  // code submissions, so the two code-specific tabs (Code Signals, Code
  // Understanding) are hidden and the page opens on the Timeline. Everything
  // else (identity / snapshots / room scan / timeline) is test-type-agnostic —
  // same MediaPipe + snapshot events, same test_id — so those tabs stay.
  const NON_CODING_TYPES = new Set(['apt', 'screening', 'pnb', 'communication']);
  const isNonCoding = NON_CODING_TYPES.has(typeProp || searchParams.get('type'));
  const visibleTabs = isNonCoding
    ? TABS.filter((t) => t.id !== 'signals' && t.id !== 'understanding')
    : TABS;

  const [activeTab, setActiveTab] = useState(isNonCoding ? 'timeline' : 'signals');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // MOSS diff modal state (lifted up so a click anywhere in Signals tab
  // can open it without prop-drilling through every row).
  const [diffOpen, setDiffOpen] = useState(false);
  const [diffPayload, setDiffPayload] = useState(null);

  // PDF export — disabled-while-generating to prevent double-clicks
  // (server fetches S3 images + reportlab render → can take 3-5s on a
  // noisy test with many snapshots).
  const [pdfBusy, setPdfBusy] = useState(false);
  const handleExportPdf = async () => {
    if (!testId || pdfBusy) return;
    setPdfBusy(true);
    try {
      const r = await fetch(
        `${API_BASE_URL}/api/cheating/dashboard/${encodeURIComponent(testId)}/pdf`,
        { credentials: 'include' },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      // Trigger save-as via a temporary anchor — works in every browser,
      // no popup-blocker issues vs window.open.
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Server already sets Content-Disposition with the proper filename;
      // we set a fallback here in case the browser ignores the header.
      const cand = data?.identity?.candidate_email?.split('@')[0] || 'candidate';
      a.download = `cheating-evidence_${cand}_${testId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Release the blob after the click handler runs.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      // Surface to the user — silent failure on an "Export" button is
      // worse than a clear error message.
      // eslint-disable-next-line no-alert
      alert(`PDF export failed: ${e.message || e}`);
    } finally {
      setPdfBusy(false);
    }
  };

  useEffect(() => {
    if (!testId) {
      setError('No test_id provided');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await fetch(
          `${API_BASE_URL}/api/cheating/dashboard/${encodeURIComponent(testId)}`,
          { credentials: 'include' },
        );
        if (r.status === 403) {
          throw new Error('Sign in with an account that can view this candidate, then open this link again.');
        }
        if (!r.ok) {
          const j = await r.json().catch(() => ({}));
          throw new Error(j.error || `HTTP ${r.status}`);
        }
        const j = await r.json();
        if (!cancelled) setData(j);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load dashboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [testId]);

  const openDiff = ({ questionIdx, peerTestId, candidateLabel, peerLabel }) => {
    setDiffPayload({ questionIdx, peerTestId, candidateLabel, peerLabel });
    setDiffOpen(true);
  };

  // When embedded, the parent owns the page chrome — drop the
  // min-h-screen + bg-slate-50 styling so we slot in cleanly inside the
  // parent's existing layout (matches Reports.jsx's embedded mode).
  // Standalone mode keeps its full-page look.
  const outerCls = embedded
    ? "bg-transparent"
    : "min-h-screen bg-slate-50";
  const innerCls = embedded
    ? ""                         // parent supplies its own max-width/padding
    : "max-w-7xl mx-auto px-6 py-8";

  // "Back" → close the embedded view if the parent gave us a closer;
  // else fall back to browser history (the standalone route case).
  const handleBack = () => {
    if (typeof onClose === 'function') {
      onClose();
    } else {
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <div className={`${outerCls} flex items-center justify-center`}>
        <div className="text-slate-500 py-12">Loading evidence…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${outerCls} flex flex-col items-center justify-center gap-3 py-12`}>
        <AlertTriangle className="w-10 h-10 text-amber-500" />
        <div className="text-slate-700 font-medium">{error}</div>
        <button onClick={handleBack} className="text-sm text-blue-600 underline">
          Go back
        </button>
      </div>
    );
  }

  return (
    <div className={outerCls}>
      <div className={innerCls}>

        {/* Back link + page title + Export PDF */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={handleBack}
            className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <div className="text-slate-300">/</div>
          <h1 className="text-xl font-semibold text-slate-900">
            Cheating Evidence
          </h1>
          <code className="text-xs text-slate-500 ml-2">{testId}</code>

          {/* Export PDF — sits on the right of the top bar. Hidden while
              the dashboard is still loading the JSON payload (no point
              offering a PDF when we don't even have data yet). */}
          <div className="ml-auto">
            <button
              onClick={handleExportPdf}
              disabled={pdfBusy || !data}
              className={
                'inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-md border transition ' +
                (pdfBusy || !data
                  ? 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed'
                  : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50 hover:border-slate-400 shadow-sm')
              }
              title="Generate a print-ready PDF report of this evidence dashboard"
            >
              {pdfBusy ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Preparing PDF…
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Export PDF
                </>
              )}
            </button>
          </div>
        </div>

        <VerdictHeader verdict={data?.verdict} />

        {/* Plain-English narrative — sits between the raw verdict header
            and the tabs so recruiters get a 2-3 sentence summary BEFORE
            they have to dig through the per-signal panels. Hidden when
            the narrative has no headline (clean defaults still render
            via the header above). */}
        <NarrativePanel narrative={data?.narrative} />

        {/* Tab nav */}
        <div className="mt-6 border-b border-slate-200 flex gap-1 overflow-x-auto">
          {visibleTabs.map((t) => {
            const Icon = t.icon;
            const active = activeTab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={
                  'px-4 py-2.5 text-sm font-medium border-b-2 transition flex items-center gap-2 whitespace-nowrap ' +
                  (active
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-slate-500 hover:text-slate-700')
                }
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="mt-6">
          {/* Code Understanding tab — Phase 5. Renders the per-question
              voice follow-up data straight from data.code_submissions[i].
              followup_session. The tab component handles all three render
              states (taken / not-attempted / mic-denied) internally so
              this branch stays a one-liner. */}
          {activeTab === 'understanding' && (
            <CodeUnderstandingTab
              codeSubmissions={data?.code_submissions}
            />
          )}

          {activeTab === 'signals' && (
            <SignalsTab
              signals={data?.signals}
              codeSubmissions={data?.code_submissions}
              onOpenDiff={openDiff}
            />
          )}
          {activeTab === 'identity' && (
            <IdentityTab identity={data?.identity} />
          )}
          {activeTab === 'snapshots' && (
            <SnapshotsTab snapshots={data?.snapshots} />
          )}
          {activeTab === 'baseline' && (
            <BaselineTab baseline={data?.baseline} />
          )}
          {activeTab === 'timeline' && (
            <TimelineTab timeline={data?.timeline} />
          )}
        </div>
      </div>

      {/* MOSS diff modal — lifted to page-level so it overlays the whole UI */}
      {diffOpen && diffPayload && (
        <MossDiffModal
          testId={testId}
          questionIdx={diffPayload.questionIdx}
          peerTestId={diffPayload.peerTestId}
          candidateLabel={diffPayload.candidateLabel}
          peerLabel={diffPayload.peerLabel}
          onClose={() => setDiffOpen(false)}
        />
      )}
    </div>
  );
}

// ─── Verdict header ──────────────────────────────────────────────────────────
function VerdictHeader({ verdict }) {
  // Hooks must be called unconditionally at the top — useMemo before any
  // early return. The memo handles null/undefined verdict gracefully so
  // we can still bail out below.
  const drivingSignals = useMemo(() => {
    const pf = (verdict && verdict.per_feature) || {};
    return Object.entries(pf)
      .map(([k, v]) => ({ key: k, score: v.score || 0, count: v.count || 0 }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);
  }, [verdict]);

  if (!verdict || verdict.error) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-5 text-slate-500">
        Verdict unavailable.
      </div>
    );
  }
  const band = VERDICT_BAND[verdict.verdict] || VERDICT_BAND.clean;

  return (
    <div className={`rounded-lg border p-5 ${band.cls}`}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          {band.icon}
          <div>
            <div className="text-xs uppercase tracking-wide opacity-70">Verdict</div>
            <div className="text-2xl font-semibold">{band.label}</div>
          </div>
        </div>
        <div className="border-l border-current/20 pl-4 ml-2">
          <div className="text-xs uppercase tracking-wide opacity-70">Total Score</div>
          <div className="text-2xl font-semibold">{Math.round(verdict.total_score || 0)}</div>
        </div>
        <div className="border-l border-current/20 pl-4">
          <div className="text-xs uppercase tracking-wide opacity-70">Events</div>
          <div className="text-2xl font-semibold">{verdict.event_count || 0}</div>
        </div>

        <div className="ml-auto max-w-md">
          <div className="text-xs uppercase tracking-wide opacity-70 mb-1">
            Top driving signals
          </div>
          {drivingSignals.length === 0 ? (
            <div className="text-sm opacity-70">No signals fired.</div>
          ) : (
            <ul className="text-sm space-y-0.5">
              {drivingSignals.map((s) => (
                <li key={s.key} className="flex justify-between gap-3">
                  <span>{niceFeatureName(s.key)}</span>
                  <span className="font-medium">+{Math.round(s.score)} ({s.count})</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <div className="mt-3 text-sm opacity-90">{band.sub}</div>
    </div>
  );
}

// ─── Plain-English narrative panel ───────────────────────────────────────────
//
// Sits between the verdict header (band + score + driving signals) and the
// tabs (per-signal drill-down). The goal is to let a non-technical recruiter
// or hiring manager understand the verdict in under 10 seconds without
// having to click any tab — TL;DR up top, ranked reasons in the middle,
// recommended action at the bottom.
//
// Reads from `data.narrative` in the dashboard payload (composed
// server-side by services/cheating/verdict_narrative.py).
function NarrativePanel({ narrative }) {
  if (!narrative || !narrative.headline) return null;

  // Tone-driven styling — clean is a soft positive frame, suspicious is
  // amber, high_confidence is red. Keeps the narrative panel visually
  // consistent with the band chip in VerdictHeader above.
  const TONE_STYLES = {
    clean:           'border-emerald-200 bg-emerald-50',
    minor:           'border-blue-200 bg-blue-50',
    suspicious:      'border-amber-200 bg-amber-50',
    high_confidence: 'border-red-200 bg-red-50',
  };
  const ACTION_STYLES = {
    clean:           'bg-emerald-100 text-emerald-800 border-emerald-200',
    minor:           'bg-blue-100 text-blue-800 border-blue-200',
    suspicious:      'bg-amber-100 text-amber-800 border-amber-200',
    high_confidence: 'bg-red-100 text-red-800 border-red-200',
  };
  const cls = TONE_STYLES[narrative.tone] || TONE_STYLES.clean;
  const actionCls = ACTION_STYLES[narrative.tone] || ACTION_STYLES.clean;

  return (
    <div className={`mt-4 rounded-lg border p-5 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-1">
        Why was this flagged?
      </div>
      <div className="text-base font-medium text-slate-900 leading-snug">
        {narrative.headline}
      </div>

      {Array.isArray(narrative.reasons) && narrative.reasons.length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {narrative.reasons.map((r, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-slate-800">
              <span className="text-slate-400 mt-0.5">•</span>
              <span>{r}</span>
            </li>
          ))}
        </ul>
      )}

      {narrative.recommended_action && (
        <div className={`mt-4 inline-flex items-start gap-2 px-3 py-2 rounded border text-sm ${actionCls}`}>
          <span className="text-xs uppercase tracking-wide font-semibold opacity-70 shrink-0 mt-0.5">
            Recommended:
          </span>
          <span>{narrative.recommended_action}</span>
        </div>
      )}

      {narrative.evidence_count > 0 && (
        <div className="mt-3 text-[11px] text-slate-500">
          Based on {narrative.evidence_count} detection event{narrative.evidence_count === 1 ? '' : 's'}.
          See the tabs below for full evidence.
        </div>
      )}
    </div>
  );
}

// ─── Tab 1: Signals (Layer 6 code-plagiarism breakdown) ──────────────────────
function SignalsTab({ signals, codeSubmissions, onOpenDiff }) {
  if (!signals) {
    return <EmptyState text="No code submissions analysed yet." />;
  }
  const {
    cross_candidate = [],
    ai_generated = [],
    online_match = [],
    cross_question_style = [],
  } = signals;

  // Per-question attempt status — used to mark questions the candidate
  // didn't actually try (submission was essentially boilerplate). We
  // skip plagiarism analysis for those server-side, so they won't have
  // signals; the summary card below tells the recruiter that explicitly
  // instead of leaving them confused about missing data.
  const submissions = Array.isArray(codeSubmissions) ? codeSubmissions : [];
  const notAttemptedQs = submissions
    .filter((s) => s && s.was_attempted === false)
    .map((s) => s.question_idx)
    .sort((a, b) => a - b);
  const attemptedCount = submissions.length - notAttemptedQs.length;

  const allEmpty =
    !cross_candidate.length &&
    !ai_generated.length &&
    !online_match.length &&
    !cross_question_style.length;
  if (allEmpty && !notAttemptedQs.length) {
    return <EmptyState text="No code-plagiarism signals fired for this candidate." />;
  }

  return (
    <div className="space-y-4">
      {/* Attempt summary — shown FIRST so recruiter knows immediately
          that some questions weren't attempted and any plagiarism
          numbers below are averaged across attempted questions only. */}
      {notAttemptedQs.length > 0 && (
        <AttemptSummaryCard
          attemptedCount={attemptedCount}
          notAttemptedQs={notAttemptedQs}
          totalQs={submissions.length}
        />
      )}

      {/* Per-question raw scores — shows the actual MOSS / AI / Online
          percentages for every ATTEMPTED question, even when none of
          them crossed the alert threshold. Explains where the
          plagiarism_percentage shown on Reports.jsx comes from. Hidden
          when there's no raw-score data (e.g., legacy tests scored
          before this column existed). */}
      <PerQuestionScoresCard submissions={submissions} />

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <SignalCard
        title="Cross-candidate similarity (MOSS)"
        subtitle="Code overlap with other candidates' submissions"
        icon={<Hash className="w-4 h-4" />}
        emptyText="No high-similarity matches with other candidates."
        items={cross_candidate}
        renderItem={(it, i) => (
          <SignalRow
            key={i}
            severity={it.severity}
            heading={`Q${(it.question_idx ?? 0) + 1} — ${Math.round(it.similarity_pct || 0)}% match`}
            sub={
              <>
                Peer: <code className="text-xs">{it.peer_email || it.peer_test_id || 'unknown'}</code>
                {it.threshold_pct && (
                  <> · threshold {Math.round(it.threshold_pct)}%</>
                )}
              </>
            }
            action={
              it.peer_test_id && it.question_idx != null ? (
                <button
                  onClick={() =>
                    onOpenDiff({
                      questionIdx: it.question_idx,
                      peerTestId: it.peer_test_id,
                      candidateLabel: 'Candidate',
                      peerLabel: it.peer_email || it.peer_test_id,
                    })
                  }
                  className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                >
                  view diff <ExternalLink className="w-3 h-3" />
                </button>
              ) : null
            }
          />
        )}
      />

      <SignalCard
        title="AI-generation likelihood"
        subtitle="LLM judge — was this code likely generated by an AI tool?"
        icon={<Brain className="w-4 h-4" />}
        emptyText="No AI-generation flags."
        items={ai_generated}
        renderItem={(it, i) => (
          <SignalRow
            key={i}
            severity={it.severity}
            heading={`Q${(it.question_idx ?? 0) + 1} — ${Math.round(it.ai_likelihood || 0)}% likely AI`}
            sub={it.summary || it.message}
            evidence={it.ai_evidence}
          />
        )}
      />

      <SignalCard
        title="Online-source match"
        subtitle="LLM judge — does this look like a copy from a known online source?"
        icon={<FileSearch className="w-4 h-4" />}
        emptyText="No online-source flags."
        items={online_match}
        renderItem={(it, i) => (
          <SignalRow
            key={i}
            severity={it.severity}
            heading={`Q${(it.question_idx ?? 0) + 1} — ${Math.round(it.online_likelihood || 0)}% likely copied`}
            sub={it.summary || it.message}
            evidence={it.online_evidence}
          />
        )}
      />

      <SignalCard
        title="Cross-question style shift"
        subtitle="Style consistency between this candidate's own answers"
        icon={<Cpu className="w-4 h-4" />}
        emptyText="No style-shift flags."
        items={cross_question_style}
        renderItem={(it, i) => (
          <SignalRow
            key={i}
            severity={it.severity}
            heading={`Q${(it.question_idx ?? 0) + 1} vs Q${(it.vs_question_idx ?? 0) + 1}`}
            sub={
              <>
                Style distance {(it.style_distance ?? 0).toFixed(2)}
                {it.threshold && <> · threshold {Number(it.threshold).toFixed(2)}</>}
              </>
            }
          />
        )}
      />
    </div>
    </div>
  );
}

// Banner card shown above the per-signal grid when the candidate didn't
// attempt one or more questions. Tells recruiters explicitly that any
// plagiarism % shown is computed across attempted questions only —
// preventing the "wait, why does this candidate have only 1 finding
// listed when there were 4 questions?" confusion.
function AttemptSummaryCard({ attemptedCount, notAttemptedQs, totalQs }) {
  const qList = notAttemptedQs.map((idx) => `Q${idx + 1}`).join(', ');
  const allSkipped = attemptedCount === 0;
  return (
    <div
      className={
        'rounded-lg border p-4 ' +
        (allSkipped
          ? 'bg-red-50 border-red-200'
          : 'bg-amber-50 border-amber-200')
      }
    >
      <div className="flex items-start gap-3">
        <AlertTriangle
          className={
            'w-5 h-5 shrink-0 mt-0.5 ' +
            (allSkipped ? 'text-red-600' : 'text-amber-600')
          }
        />
        <div className="flex-1 min-w-0">
          <div className={
            'text-sm font-semibold ' +
            (allSkipped ? 'text-red-900' : 'text-amber-900')
          }>
            {allSkipped
              ? `Candidate did not attempt any of the ${totalQs} questions`
              : `${notAttemptedQs.length} of ${totalQs} question${totalQs === 1 ? '' : 's'} not attempted: ${qList}`}
          </div>
          <div className={
            'text-xs mt-1 ' +
            (allSkipped ? 'text-red-700' : 'text-amber-700')
          }>
            Plagiarism analysis below is based on the {attemptedCount} question{attemptedCount === 1 ? '' : 's'}
            {attemptedCount === 0 ? ' the candidate attempted' : ' the candidate genuinely attempted'}.
            Boilerplate-only submissions (skipped questions) are excluded so
            they don't produce false-positive matches.
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * PerQuestionScoresCard — shows the raw per-question MOSS / AI / Online
 * plagiarism percentages for every ATTEMPTED question, even when they
 * were below the event-emission threshold (currently 70%).
 *
 * Why this exists: previously the Signals tab showed nothing when no
 * signals crossed threshold, while the Reports.jsx PlagiarismCard
 * could still show e.g. "25%" — because plagiarism_percentage is fed
 * by RAW per-question maxes, including sub-threshold ones. Recruiters
 * saw a contradiction with no explanation. This card makes the raw
 * scores visible inline so both surfaces tell the same story.
 *
 * Thresholds (must stay in sync with backend's emit logic):
 *   MOSS:    flag when ≥ 70% (admin-configurable in feature_flags)
 *   AI:      flag when ≥ 70%
 *   Online:  flag when ≥ 70%
 *
 * A score is rendered RED when ≥ 70% (would have triggered an event),
 * AMBER when 40-69% (notable but below alert), or GRAY when < 40%
 * (low signal). Threshold tooltip explains each.
 */
function PerQuestionScoresCard({ submissions }) {
  const attempted = (submissions || []).filter(
    (s) => s && s.was_attempted !== false && s.raw_scores,
  );
  if (attempted.length === 0) return null;

  // Helper to render one score cell with appropriate color
  const _scoreCell = (value, threshold = 70) => {
    const n = Number(value) || 0;
    let cls = 'text-slate-400';
    if (n >= threshold) cls = 'text-red-700 font-semibold';
    else if (n >= 40) cls = 'text-amber-700';
    else if (n > 0) cls = 'text-slate-700';
    return (
      <span className={cls} title={n >= threshold ? `Crossed ${threshold}% alert threshold` : `Below ${threshold}% alert threshold`}>
        {Math.round(n)}%
      </span>
    );
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 mb-4">
      <div className="p-4 border-b border-slate-100">
        <div className="text-sm font-semibold text-slate-900">
          Per-Question Plagiarism Scores
        </div>
        <div className="text-xs text-slate-500 mt-0.5">
          Raw scores for every attempted question. Values ≥ 70% (red) crossed the alert threshold and fired an event in the cards below. Sub-threshold values are still part of the overall plagiarism average.
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left text-xs uppercase text-slate-500">
              <th className="px-4 py-2 font-medium">Question</th>
              <th className="px-4 py-2 font-medium text-right">MOSS (peer overlap)</th>
              <th className="px-4 py-2 font-medium text-right">AI Generation</th>
              <th className="px-4 py-2 font-medium text-right">Online Match</th>
              <th className="px-4 py-2 font-medium text-right">Q Max</th>
            </tr>
          </thead>
          <tbody>
            {attempted.map((s) => {
              const rs = s.raw_scores || {};
              const moss = Number(rs.moss_max_pct) || 0;
              const ai = Number(rs.ai_likelihood) || 0;
              const online = Number(rs.online_likelihood) || 0;
              const qMax = Math.max(moss, ai, online);
              return (
                <tr key={s.question_idx} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-medium text-slate-700">
                    Q{(s.question_idx ?? 0) + 1}
                  </td>
                  <td className="px-4 py-2 text-right">{_scoreCell(moss)}</td>
                  <td className="px-4 py-2 text-right">{_scoreCell(ai)}</td>
                  <td className="px-4 py-2 text-right">{_scoreCell(online)}</td>
                  <td className="px-4 py-2 text-right font-semibold">
                    {_scoreCell(qMax)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SignalCard({ title, subtitle, icon, items, emptyText, renderItem }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200">
      <div className="p-4 border-b border-slate-100">
        <div className="flex items-center gap-2 text-slate-900 font-semibold">
          {icon} {title}
        </div>
        <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>
      </div>
      <div className="divide-y divide-slate-100">
        {items.length === 0 ? (
          <div className="p-4 text-sm text-slate-500">{emptyText}</div>
        ) : (
          items.map((it, i) => renderItem(it, i))
        )}
      </div>
    </div>
  );
}

function SignalRow({ severity, heading, sub, action, evidence }) {
  const [expanded, setExpanded] = useState(false);
  const hasEvidence = Array.isArray(evidence) && evidence.length > 0;
  return (
    <div className="p-3 hover:bg-slate-50">
      <div className="flex items-start gap-3">
        <span className={`text-xs px-2 py-0.5 rounded border ${SEVERITY_BADGE[severity] || SEVERITY_BADGE.info}`}>
          {severity}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-slate-900">{heading}</div>
          {sub && <div className="text-xs text-slate-600 mt-0.5">{sub}</div>}
          {hasEvidence && (
            <button
              onClick={() => setExpanded((e) => !e)}
              className="text-xs text-slate-500 hover:text-slate-900 mt-1.5 flex items-center gap-1"
            >
              {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              {evidence.length} evidence item{evidence.length === 1 ? '' : 's'}
            </button>
          )}
          {expanded && hasEvidence && (
            <ul className="mt-1.5 ml-4 list-disc text-xs text-slate-700 space-y-0.5">
              {evidence.map((e, i) => (
                <li key={i}>{typeof e === 'string' ? e : JSON.stringify(e)}</li>
              ))}
            </ul>
          )}
        </div>
        {action && <div className="ml-2">{action}</div>}
      </div>
    </div>
  );
}

// ─── Tab 2: Identity ─────────────────────────────────────────────────────────
function IdentityTab({ identity }) {
  if (!identity) {
    return <EmptyState text="No identity verification record for this test." />;
  }
  const verdict = identity.verdict || 'pending';
  const verdictCls =
    verdict === 'passed' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' :
    verdict === 'failed' ? 'bg-red-50 border-red-200 text-red-700' :
    verdict === 'skipped' ? 'bg-slate-50 border-slate-200 text-slate-600' :
    verdict === 'manual_review' ? 'bg-amber-50 border-amber-200 text-amber-800' :
    'bg-blue-50 border-blue-200 text-blue-700';

  return (
    <div className="space-y-4">
      <div className={`rounded-lg border p-4 ${verdictCls}`}>
        <div className="text-xs uppercase tracking-wide opacity-70">Verdict</div>
        <div className="text-xl font-semibold capitalize">{verdict.replace('_', ' ')}</div>
        {identity.verdict_reason && (
          <div className="text-sm mt-1 opacity-90">{identity.verdict_reason}</div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <IdentityImageCard
          title="ID Photo"
          url={identity.id_photo_url}
          subtitle={
            identity.id_face_confidence != null
              ? `Face confidence ${identity.id_face_confidence}%`
              : 'No ID uploaded'
          }
        />
        <IdentityImageCard
          title="Live Selfie"
          url={identity.selfie_url}
          subtitle={
            identity.selfie_s3_key ? 'Captured at test start' : 'No selfie captured'
          }
        />
      </div>

      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="text-sm font-semibold text-slate-900 mb-3">Face match</div>
        <KV k="Similarity" v={identity.match_similarity != null ? `${identity.match_similarity}%` : '—'} />
        <KV k="Threshold" v={identity.match_threshold != null ? `${identity.match_threshold}%` : '—'} />
        <KV k="Result" v={
          identity.match_similarity != null && identity.match_threshold != null ? (
            identity.match_similarity >= identity.match_threshold
              ? <span className="text-emerald-700 font-medium">PASS</span>
              : <span className="text-red-700 font-medium">FAIL</span>
          ) : '—'
        } />
      </div>

      {identity.liveness_status && (
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="text-sm font-semibold text-slate-900 mb-3">Liveness check</div>
          <KV k="Status" v={identity.liveness_status} />
          <KV k="Confidence" v={identity.liveness_confidence != null ? `${identity.liveness_confidence}%` : '—'} />
          <KV k="Session" v={<code className="text-xs">{identity.liveness_session_id || '—'}</code>} />
        </div>
      )}
    </div>
  );
}

function IdentityImageCard({ title, url, subtitle }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="p-3 border-b border-slate-100">
        <div className="text-sm font-semibold text-slate-900">{title}</div>
        <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>
      </div>
      <div className="aspect-square bg-slate-100 flex items-center justify-center">
        {url ? (
          <img
            src={url}
            alt={title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="text-sm text-slate-400">No image</div>
        )}
      </div>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex justify-between py-1 text-sm">
      <span className="text-slate-500">{k}</span>
      <span className="text-slate-900">{v}</span>
    </div>
  );
}

// ─── Tab 3: Snapshots ────────────────────────────────────────────────────────
function SnapshotsTab({ snapshots }) {
  const [selected, setSelected] = useState(null);
  if (!snapshots || snapshots.length === 0) {
    return <EmptyState text="No snapshots taken during this test." />;
  }
  return (
    <div className="space-y-4">
      <div className="text-sm text-slate-600">
        {snapshots.length} snapshot{snapshots.length === 1 ? '' : 's'} captured during the test.
        Click a thumbnail to expand.
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {snapshots.map((s) => (
          <button
            key={s.event_id}
            onClick={() => setSelected(s)}
            className="bg-white rounded border border-slate-200 overflow-hidden hover:border-blue-400 transition text-left"
          >
            <div className="aspect-video bg-slate-100">
              {s.url ? (
                <img src={s.url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xs text-slate-400">
                  No image
                </div>
              )}
            </div>
            <div className="p-2">
              <div className="flex items-center gap-1">
                <span className={`text-xs px-1.5 py-0.5 rounded border ${SEVERITY_BADGE[s.severity] || SEVERITY_BADGE.info}`}>
                  {s.severity}
                </span>
              </div>
              <div className="text-xs text-slate-600 mt-1 line-clamp-1">
                {s.message || niceFeatureName(s.feature_key)}
              </div>
              <div className="text-[10px] text-slate-400 mt-0.5">{formatTime(s.occurred_at)}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Modal for selected snapshot */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="font-semibold text-slate-900">
                  {niceFeatureName(selected.feature_key)}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {formatTime(selected.occurred_at)}
                </div>
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="bg-slate-100">
              {selected.url ? (
                <img src={selected.url} alt="" className="w-full h-auto" />
              ) : (
                <div className="aspect-video flex items-center justify-center text-slate-400">
                  No image
                </div>
              )}
            </div>
            <div className="p-4 space-y-2">
              <KV k="Severity" v={selected.severity} />
              <KV k="Reason" v={selected.snapshot_reason || '—'} />
              {selected.message && (
                <div className="text-sm text-slate-700 pt-2 border-t border-slate-100">
                  {selected.message}
                </div>
              )}
              {Array.isArray(selected.labels) && selected.labels.length > 0 && (
                <div className="pt-2 border-t border-slate-100">
                  <div className="text-xs text-slate-500 mb-1">Detected labels:</div>
                  <div className="flex flex-wrap gap-1">
                    {selected.labels.map((l, i) => (
                      <span key={i} className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                        {typeof l === 'string' ? l : (l.name || JSON.stringify(l))}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {selected.detected_text && (
                <div className="pt-2 border-t border-slate-100">
                  <div className="text-xs text-slate-500 mb-1">Detected text:</div>
                  <pre className="text-xs bg-slate-50 p-2 rounded whitespace-pre-wrap break-all">
                    {Array.isArray(selected.detected_text)
                      ? selected.detected_text.join('\n')
                      : selected.detected_text}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab 4: Baseline (room scan) ─────────────────────────────────────────────
function BaselineTab({ baseline }) {
  if (!baseline) {
    return <EmptyState text="No baseline room scan for this test." />;
  }
  const frames = baseline.frame_urls || [];
  const catalog = Array.isArray(baseline.object_catalog) ? baseline.object_catalog : [];
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="text-sm font-semibold text-slate-900 mb-3">Scan summary</div>
        <KV k="Frames captured" v={baseline.frame_count ?? frames.length} />
        <KV k="Sweep duration" v={baseline.sweep_duration_seconds ? `${baseline.sweep_duration_seconds.toFixed(1)}s` : '—'} />
        <KV k="Verdict" v={baseline.verdict || '—'} />
        {baseline.verdict_reason && (
          <div className="text-sm text-slate-600 pt-2 border-t border-slate-100 mt-2">
            {baseline.verdict_reason}
          </div>
        )}
      </div>

      {frames.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="text-sm font-semibold text-slate-900 mb-3">Sweep frames</div>
          <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-2">
            {frames.map((f, i) => (
              <div key={i} className="aspect-video bg-slate-100 rounded overflow-hidden">
                {f.url ? (
                  <img src={f.url} alt={`frame ${i}`} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs text-slate-400">
                    {`frame ${i}`}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {catalog.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="text-sm font-semibold text-slate-900 mb-3">
            Detected objects ({catalog.length})
          </div>
          <div className="flex flex-wrap gap-2">
            {catalog.map((o, i) => (
              <span
                key={i}
                className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700 border border-slate-200"
                title={`Max confidence ${o.max_confidence}% across ${o.frame_count} frames`}
              >
                {o.name} · {Math.round(o.max_confidence ?? 0)}%
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab 5: Timeline ─────────────────────────────────────────────────────────
function TimelineTab({ timeline }) {
  const [severityFilter, setSeverityFilter] = useState('all');
  const [featureFilter, setFeatureFilter] = useState('all');

  // Build distinct lists for the filter pills from whatever's in the data.
  const features = useMemo(() => {
    const s = new Set();
    (timeline || []).forEach((e) => e.feature_key && s.add(e.feature_key));
    return Array.from(s).sort();
  }, [timeline]);

  const filtered = useMemo(() => {
    return (timeline || []).filter((e) => {
      if (severityFilter !== 'all' && e.severity !== severityFilter) return false;
      if (featureFilter !== 'all' && e.feature_key !== featureFilter) return false;
      return true;
    });
  }, [timeline, severityFilter, featureFilter]);

  if (!timeline || timeline.length === 0) {
    return <EmptyState text="No cheating events recorded for this test." />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="text-slate-500">Severity:</span>
        {['all', 'info', 'low', 'medium', 'high', 'critical'].map((s) => (
          <FilterPill
            key={s}
            active={severityFilter === s}
            onClick={() => setSeverityFilter(s)}
          >
            {s}
          </FilterPill>
        ))}
        <span className="text-slate-500 ml-3">Feature:</span>
        <select
          value={featureFilter}
          onChange={(e) => setFeatureFilter(e.target.value)}
          className="text-xs border border-slate-200 rounded px-2 py-1 bg-white"
        >
          <option value="all">All</option>
          {features.map((f) => (
            <option key={f} value={f}>{niceFeatureName(f)}</option>
          ))}
        </select>
        <span className="text-slate-400 ml-auto">
          {filtered.length} of {timeline.length} events
        </span>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 divide-y divide-slate-100">
        {filtered.map((e) => (
          <TimelineRow key={e.id} event={e} />
        ))}
      </div>
    </div>
  );
}

function FilterPill({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={
        'px-2 py-0.5 rounded border ' +
        (active
          ? 'bg-blue-600 border-blue-600 text-white'
          : 'bg-white border-slate-200 text-slate-600 hover:border-slate-400')
      }
    >
      {children}
    </button>
  );
}

// ─── HR-readable evidence rendering ─────────────────────────────────────────
// The evidence JSONB is heterogeneous (every detector attaches different
// fields), and raw JSON means nothing to a recruiter. Flatten it into
// labeled rows with curated names; hide internal/technical fields; anything
// too complex to flatten stays behind a "technical details" toggle.

const EVIDENCE_HIDE_KEYS = new Set([
  'snapshot_s3_key', 's3_key', 'snapshot_url', 'application_id',
  'candidate_email', 'test_id',
]);

const EVIDENCE_LABELS = {
  stage: 'Alert stage',
  warn_after_seconds: 'Warned after (seconds)',
  flag_after_seconds: 'Flagged after (seconds)',
  face_count: 'Faces in frame',
  snapshot_reason: 'Capture type',
  trigger_feature: 'Triggered by',
  detectors_started: 'Active detectors',
  glance_count: 'Off-screen glances',
  duration_seconds: 'Duration (seconds)',
  seconds_without_face: 'Seconds without a face',
  labels: 'Objects detected',
  detected_text: 'Text detected on screen',
};

const EVIDENCE_VALUE_LABELS = {
  scheduled: 'Routine (timed capture)',
  event_triggered: 'Triggered by a detection',
  warn: 'Warning shown to candidate',
  flag: 'Flagged for review',
};

function friendlyEvidenceRows(evidence) {
  const rows = [];
  const leftovers = {};
  if (!evidence || typeof evidence !== 'object' || Array.isArray(evidence)) {
    return { rows, leftovers: null };
  }
  for (const [key, value] of Object.entries(evidence)) {
    if (value === null || value === undefined || value === '') continue;
    if (EVIDENCE_HIDE_KEYS.has(key) || /(_s3_key|_key|_url)$/.test(key)) continue;

    const label = EVIDENCE_LABELS[key] || humanize(key);
    if (typeof value === 'boolean') {
      rows.push({ label, value: value ? 'Yes' : 'No' });
    } else if (typeof value === 'number') {
      rows.push({ label, value: String(value) });
    } else if (typeof value === 'string') {
      const pretty = EVIDENCE_VALUE_LABELS[value]
        || (value.includes('.') ? niceFeatureName(value) : value);
      rows.push({ label, value: pretty });
    } else if (Array.isArray(value) && value.every((v) => typeof v === 'string' || typeof v === 'number')) {
      rows.push({
        label,
        value: value.map((v) => (typeof v === 'string' ? humanize(String(v)) : String(v))).join(', '),
      });
    } else {
      leftovers[key] = value; // nested objects / arrays of objects
    }
  }
  return { rows, leftovers: Object.keys(leftovers).length ? leftovers : null };
}

function FriendlyEvidence({ evidence }) {
  const [showTech, setShowTech] = useState(false);
  const { rows, leftovers } = friendlyEvidenceRows(evidence);

  if (rows.length === 0 && !leftovers) {
    return (
      <div className="mt-2 text-xs text-slate-500 italic">
        No additional detail recorded for this event.
      </div>
    );
  }
  return (
    <div className="mt-2 text-xs bg-slate-50 border border-slate-100 p-2.5 rounded space-y-1 max-h-64 overflow-auto">
      {rows.map(({ label, value }, i) => (
        <div key={i} className="flex gap-2">
          <span className="text-slate-500 min-w-[160px]">{label}:</span>
          <span className="text-slate-800">{value}</span>
        </div>
      ))}
      {leftovers && (
        <div className="pt-1">
          <button
            onClick={() => setShowTech((s) => !s)}
            className="text-slate-400 hover:text-slate-700 underline underline-offset-2"
          >
            {showTech ? 'Hide technical details' : 'Technical details'}
          </button>
          {showTech && (
            <pre className="mt-1 text-[11px] text-slate-600 overflow-auto">
              {JSON.stringify(leftovers, null, 2)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

function TimelineRow({ event }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="p-3 hover:bg-slate-50">
      <div className="flex items-start gap-3">
        <div className="text-xs text-slate-400 font-mono min-w-[120px]">
          {formatTime(event.occurred_at)}
        </div>
        <span className={`text-xs px-2 py-0.5 rounded border ${SEVERITY_BADGE[event.severity] || SEVERITY_BADGE.info}`}>
          {event.severity}
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-slate-900">
            <span className="font-medium">{niceFeatureName(event.feature_key)}</span>
            {event.score_delta ? (
              <span className="ml-2 text-xs text-slate-500">+{event.score_delta}</span>
            ) : null}
          </div>
          {event.message && (
            <div className="text-xs text-slate-600 mt-0.5">{event.message}</div>
          )}
          <button
            onClick={() => setExpanded((e) => !e)}
            className="text-xs text-slate-500 hover:text-slate-900 mt-1 flex items-center gap-1"
          >
            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            details
          </button>
          {expanded && <FriendlyEvidence evidence={event.evidence} />}
        </div>
      </div>
    </div>
  );
}

// ─── Empty state ─────────────────────────────────────────────────────────────
function EmptyState({ text }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-10 text-center text-slate-500">
      <Eye className="w-10 h-10 mx-auto mb-3 text-slate-300" />
      <div className="text-sm">{text}</div>
    </div>
  );
}
